/**
 * Footer Component
 *
 * Site footer with links and company information.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../Layout/Container';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-100 border-t">
      <Container>
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <Link to="/" className="inline-block mb-4">
                <img
                  src="/logos/logo-main.png"
                  alt="DARAK"
                  className="h-12 w-auto object-contain"
                />
              </Link>
              <p className="text-gray-600 text-sm mb-4">
                Vacation rentals across Lebanon. Chalets, villas, apartments,
                cabins. Book one and go.
              </p>
              <div className="flex gap-4">
                <SocialLink href="https://facebook.com" aria-label="Facebook">
                  <FacebookIcon />
                </SocialLink>
                <SocialLink href="https://instagram.com" aria-label="Instagram">
                  <InstagramIcon />
                </SocialLink>
                <SocialLink href="https://twitter.com" aria-label="Twitter">
                  <TwitterIcon />
                </SocialLink>
              </div>
            </div>

            {/* Explore */}
            <div>
              <h4 className="font-semibold mb-4">Explore</h4>
              <ul className="space-y-2">
                <FooterLink to="/search">Browse Properties</FooterLink>
                <FooterLink to="/search?area=batroun">Batroun</FooterLink>
                <FooterLink to="/search?area=faraya">Faraya</FooterLink>
                <FooterLink to="/search?area=ehden">Ehden</FooterLink>
                <FooterLink to="/search?area=byblos">Byblos</FooterLink>
              </ul>
            </div>

            {/* Hosting */}
            <div>
              <h4 className="font-semibold mb-4">Hosting</h4>
              <ul className="space-y-2">
                <FooterLink to="/become-host">Become a Host</FooterLink>
                <FooterLink to="/host/resources">Host Resources</FooterLink>
                <FooterLink to="/host/community">Community Forum</FooterLink>
                <FooterLink to="/host/responsible-hosting">Responsible Hosting</FooterLink>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <FooterLink to="/help">Help Center</FooterLink>
                <FooterLink to="/contact">Contact Us</FooterLink>
                <FooterLink to="/safety">Safety Information</FooterLink>
                <FooterLink to="/cancellation">Cancellation Options</FooterLink>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-6 pb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              &copy; {currentYear} DARAK. All rights reserved.
            </p>
            <nav className="flex flex-wrap gap-[18px] text-sm font-medium text-gray-600">
              <Link to="/blog" className="hover:text-gray-900">
                Blog
              </Link>
              <Link to="/help" className="hover:text-gray-900">
                Support
              </Link>
              <Link to="/privacy" className="hover:text-gray-900">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-gray-900">
                Terms
              </Link>
              <a href="/sitemap.xml" className="hover:text-gray-900">
                Sitemap
              </a>
            </nav>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 text-[13px] text-gray-500">
            <p>
              Made by{' '}
              <a
                href="https://kodefoundry.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 hover:underline"
              >
                KodeFoundry
              </a>
              .
            </p>
            <p>
              <a href="mailto:hello@kodefoundry.com" className="hover:underline">
                hello@kodefoundry.com
              </a>
            </p>
          </div>
        </div>
      </Container>
    </footer>
  );
};

interface FooterLinkProps {
  to: string;
  children: React.ReactNode;
}

const FooterLink: React.FC<FooterLinkProps> = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="text-gray-600 hover:text-gray-900 text-sm transition-colors"
    >
      {children}
    </Link>
  </li>
);

interface SocialLinkProps {
  href: string;
  children: React.ReactNode;
  'aria-label': string;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, children, ...props }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
    {...props}
  >
    {children}
  </a>
);

// Social Icons
const FacebookIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
  </svg>
);

const InstagramIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const TwitterIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

export default Footer;
